import * as Sentry from '@sentry/react';
import { api } from 'src/common/utils/api';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useAppContainerContext } from '../AppContainerContext';
import { StoredMessage } from '../chat/types';
import { useSiesta } from '../SiestaProvider';

export function useOpenConversation() {
  const { loadHistory, resumeActiveTurn, appendFailedMarker } = useSiesta();
  const { setConversationId, setSiestaMode } = useAppContainerContext();
  const { flagEnabledSiestaChats } = useFlags();

  return (id: string) => {
    void (async () => {
      try {
        const { data } = await api.get<{
          messages: StoredMessage[];
          activeTurnId: string | null;
          lastTurnFailed: boolean;
        }>(`conversations/${id}`);
        setConversationId(id);
        loadHistory(data.messages);
        setSiestaMode('chat');
        // Opening the chat shows its latest answer, so clear the unread badge. Not gated on async
        // generation: with stream persistence on, the live path completes a turn too, and nothing
        // else marks it read.
        if (flagEnabledSiestaChats) {
          if (data.activeTurnId) {
            // Still generating: the resumed stream marks it read when it finishes.
            resumeActiveTurn(id, data.activeTurnId);
          } else if (data.lastTurnFailed) {
            // The worker never returned and the sweep finalised the turn: show the give-up state
            // instead of a question that looks silently unanswered.
            appendFailedMarker();
          } else {
            api.put(`conversations/${id}/read`).catch(error => Sentry.captureException(error));
          }
        }
      } catch (error) {
        notifyAndLogError(
          'Could not open chat',
          'The chat could not be opened. Please try again.',
          error,
        );
      }
    })();
  };
}
